import { router, Link } from '@inertiajs/react'
import AppLayout from '../../components/AppLayout'
import BreakdownScoreBar, { scoreColor, fmtScore } from '../../components/BreakdownScoreBar'
import { useTheme } from '../../contexts/ThemeContext'

function ScoreBar({ value, C }) {
  const w = value != null ? Math.round(value * 100) : 0
  const color = scoreColor(value, C)
  return (
    <div style={{ minWidth: '100px', flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
        <span style={{ fontSize: '11.5px', color: C.inkMuted }}></span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11.5px', color }}>
          {fmtScore(value)} / 100
        </span>
      </div>
      <div style={{ height: '5px', background: C.paper2, borderRadius: '3px', overflow: 'hidden', border: `1px solid ${C.lineSoft}` }}>
        <div style={{ height: '100%', width: `${w}%`, background: color, borderRadius: '3px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  )
}

function GradeChip({ grade, C }) {
  if (!grade) return null
  const styles = {
    '優':    { bg: C.greenDark, color: C.paper },
    '良':    { bg: C.green,     color: C.paper },
    '可':    { bg: C.gold,      color: C.paper },
    '要注意': { bg: C.tomato,   color: C.paper },
  }
  const s = styles[grade] || { bg: C.inkMuted, color: C.paper }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', background: s.bg, color: s.color,
      fontSize: '10px', letterSpacing: '0.12em', borderRadius: '3px', fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {grade}
    </span>
  )
}

export default function WeeklyEvaluationsShow({ current_week, available_weeks, set_plans }) {
  const { theme: C } = useTheme()
  const evalCount = (set_plans || []).filter(sp => sp.evaluated).length
  const unevalCount = (set_plans || []).length - evalCount

  const handleEvaluate = (sp) => {
    if (confirm(`「${sp.set_type_name || sp.plan_code}」を${sp.evaluated ? '再評価' : '評価'}しますか？`)) {
      router.post(`/weekly_evaluations/${current_week}/set_plans/${sp.id}/evaluate`, {}, { preserveScroll: true })
    }
  }

  const handleEvaluateAll = () => {
    const msg = unevalCount > 0 ? `未評価 ${unevalCount} 件をまとめて評価しますか？` : `全 ${set_plans.length} 件を再評価しますか？`
    if (confirm(msg)) {
      router.post(`/weekly_evaluations/${current_week}/evaluate_all`, {}, { preserveScroll: true })
    }
  }

  return (
    <AppLayout bgColor={C.bg} maxWidth="1280px">
      {/* ── ツールバー ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap',
        paddingBottom: '18px', borderBottom: `1px solid ${C.line}`, marginBottom: '28px',
      }}>
        {/* 週次セレクタ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '11px', letterSpacing: '0.15em', color: C.inkMuted, textTransform: 'uppercase', fontWeight: 500 }}>
            週次
          </span>
          <div style={{ position: 'relative' }}>
            <select
              value={current_week || ''}
              onChange={e => router.visit(`/weekly_evaluations/${e.target.value}`)}
              style={{
                fontFamily: "'Noto Serif JP', serif", fontSize: '16px', fontWeight: 500,
                padding: '6px 32px 6px 12px', background: C.paper2, border: `1px solid ${C.line}`,
                borderRadius: '4px', color: C.ink, cursor: 'pointer', appearance: 'none',
              }}
            >
              {(available_weeks || []).map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '9px', color: C.inkMuted, pointerEvents: 'none' }}>▾</span>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <span style={{ fontSize: '13px', color: C.inkMuted }}>
          {evalCount} / {(set_plans || []).length} 件評価済
        </span>

        {(set_plans || []).length > 0 && (
          <button
            onClick={handleEvaluateAll}
            style={{
              fontFamily: 'inherit', fontSize: '13px', fontWeight: 500,
              padding: '8px 18px', borderRadius: '6px', border: `1px solid ${C.greenDark}`,
              background: C.greenDark, color: C.paper, cursor: 'pointer',
            }}
          >
            {unevalCount > 0 ? `全件評価（${unevalCount}件）` : '全件再評価'}
          </button>
        )}
      </div>

      {/* ── メインテーブル ── */}
      {!current_week || (set_plans || []).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: C.inkMuted, fontFamily: "'Noto Serif JP', serif", fontSize: '15px' }}>
          この週のセットプランはありません
        </div>
      ) : (
        <div style={{
          background: C.paper, borderRadius: '12px', border: `1px solid ${C.line}`,
          boxShadow: '0 4px 14px rgba(60,50,30,0.08)', overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.paper2, borderBottom: `1px solid ${C.line}` }}>
                {[
                  { label: '#',         w: '36px',  align: 'center' },
                  { label: 'セット種別', w: null,    align: 'left' },
                  { label: 'コード',     w: '100px', align: 'left' },
                  { label: 'お届けスコア', w: '260px', align: 'left' },
                  { label: '状態',       w: '70px',  align: 'center' },
                  { label: '操作',       w: '140px', align: 'right' },
                ].map((h, i) => (
                  <th key={i} style={{
                    padding: '12px 16px', textAlign: h.align, fontSize: '10.5px',
                    letterSpacing: '0.1em', color: C.inkMuted, fontWeight: 500,
                    textTransform: 'uppercase', width: h.w,
                  }}>
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {set_plans.map((sp, i) => (
                <tr
                  key={sp.id}
                  style={{
                    borderBottom: i < set_plans.length - 1 ? `1px solid ${C.lineSoft}` : 'none',
                    borderLeft: sp.evaluated ? `3px solid ${C.greenDark}` : `3px solid transparent`,
                  }}
                >
                  {/* # */}
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </td>

                  {/* セット種別 */}
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '14px', fontWeight: 500, color: C.ink }}>
                      {sp.set_type_name || '—'}
                    </span>
                  </td>

                  {/* コード */}
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: C.inkMuted }}>
                      {sp.plan_code}
                    </span>
                  </td>

                  {/* お届けスコア */}
                  <td style={{ padding: '14px 16px' }}>
                    {sp.evaluated ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BreakdownScoreBar value={sp.delivery_score} breakdown={sp.breakdown} C={C} />
                        <GradeChip grade={sp.delivery_grade} C={C} />
                      </div>
                    ) : <span style={{ color: C.inkMuted, fontSize: '12px' }}>—</span>}
                  </td>

                  {/* 状態 */}
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    {sp.evaluated ? (
                      <span style={{ fontSize: '10px', padding: '2px 8px', background: C.greenPale, color: C.greenDark, borderRadius: '3px', letterSpacing: '0.1em', fontWeight: 500 }}>
                        評価済
                      </span>
                    ) : (
                      <span style={{ fontSize: '10px', padding: '2px 8px', background: C.lineSoft, color: C.inkMuted, borderRadius: '3px', letterSpacing: '0.1em' }}>
                        未評価
                      </span>
                    )}
                  </td>

                  {/* 操作 */}
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEvaluate(sp)}
                        style={{
                          fontFamily: 'inherit', fontSize: '12px', fontWeight: 500,
                          padding: '5px 12px', borderRadius: '5px',
                          border: `1px solid ${C.line}`, background: C.paper2,
                          color: C.inkSoft, cursor: 'pointer',
                        }}
                      >
                        {sp.evaluated ? '再評価' : '評価'}
                      </button>
                      {sp.evaluated && (
                        <Link
                          href={`/weekly_evaluations/${current_week}/set_plans/${sp.id}`}
                          style={{
                            fontFamily: 'inherit', fontSize: '12px', fontWeight: 500,
                            padding: '5px 12px', borderRadius: '5px',
                            border: `1px solid ${C.greenDark}`, background: C.greenDark,
                            color: C.paper, textDecoration: 'none',
                          }}
                        >
                          詳細
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  )
}
